/**
 * DAVIDsTEA header — the account caret.
 *
 * The chevron beside the account icon is painted by dt-header as a pseudo-element on the wrapper
 * rather than being part of the control, because <shopify-account> renders its trigger inside a
 * shadow root that no theme stylesheet reaches. That leaves the caret outside the button's 44px:
 * it reads as part of the control but does nothing when clicked.
 *
 * The click is forwarded rather than the button widened. The trigger's width is declared inside
 * the shadow tree and holds against anything set on the host — 44.0 either way, even with the
 * host stretched to 64 — so there is no CSS answer from out here.
 *
 * Delegated from the document, so a header replaced by a section render in the theme editor keeps
 * working without rebinding.
 */
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const wrapper = target.closest('.account-button');
  if (!wrapper) return;

  /* A click on the icon already reached the control. It also arrives here retargeted to
     <shopify-account>, which is what stops the forwarded click below from coming back round. */
  if (target.closest('shopify-account')) return;

  const trigger = wrapper.querySelector('shopify-account')?.shadowRoot?.querySelector('button');
  trigger?.click();
});

/**
 * DAVIDsTEA header — where the account panel opens.
 *
 * <shopify-account> places its panel from `--shopify-account-dialog-position-top`. Horizon's own
 * header feeds that from `--account-offset-top`, set on its `.header` element
 * (snippets/header-actions.liquid); this theme's header is dt-header, which never sets it. So the
 * panel fell back to the component's own placement: over the header at the top of the page,
 * and well below the icon once the page had scrolled.
 *
 * Set just before the panel opens, to the bottom edge of the header bar as it sits on screen
 * then. The bar is sticky, so that is directly under the icon whether the page is at the top or
 * scrolled. Pointer and keyboard both, in the capture phase so it lands ahead of the
 * component's own handler; the event arrives retargeted to <shopify-account> from its shadow
 * tree, which is all `closest` needs.
 */
const placeAccountPanel = (/** @type {Event} */ event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const account = target.closest('.account-button')?.querySelector('shopify-account');
  const bar = account?.closest('.dt-header')?.querySelector('.dt-header__bar');
  if (!account || !bar) return;

  const bottom = Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
  account.style.setProperty('--shopify-account-dialog-position-top', `${bottom}px`);
};

document.addEventListener('pointerdown', placeAccountPanel, true);
document.addEventListener('focusin', placeAccountPanel, true);
